import type { StoryCtx } from './ctx.js';
import { mentionsIn, refIndex } from './refs.js';
import { kindForCategory } from './validate.js';
import type { Entity, FieldValue, StoryNode } from './types.js';

/** Abstand neuer Instanzen nebeneinander */
export const AUTO_INSTANCE_STEP = 260;

/**
 * Alle Texte eines Nodes, in denen Verweise stehen können: Textfelder, zusätzliche Felder und alle Textdokumente (auch der Draft). Aus
 * ihnen bekommt eine Szene ihre Objekte: Jeder hier verknüpfte Eintrag liegt als Instanz in ihr.
 */
export function textsOf(n: Pick<StoryNode, 'fields' | 'dynamicFields' | 'documents'>): string[] {
  const str = (v: FieldValue | undefined): v is string => typeof v === 'string';
  return [...Object.values(n.fields).filter(str), ...n.dynamicFields.map((f) => f.value).filter(str), ...n.documents.map((d) => d.text)];
}

/**
 * Einträge, auf die in den Texten einer Szene verwiesen wird und die noch keine Instanz in ihr haben (ihre Besetzung). Foundationals und
 * unbekannte Schlüssel zählen nicht.
 */
export function missingMentioned(ctx: StoryCtx, node: StoryNode, texts: string[]): Entity[] {
  if (node.kind !== 'event' && node.kind !== 'period') return [];
  const refs = refIndex(ctx);
  const present = new Set((ctx.children.get(node.id) ?? []).filter((c) => c.kind === 'instance').map((c) => c.entityId));
  const out: Entity[] = [];
  for (const key of new Set(texts.flatMap((t) => mentionsIn(t).map((m) => m.key)))) {
    const entity = refs.get(key);
    if (!entity || present.has(entity.id)) continue;
    const type = ctx.types.get(entity.typeId);
    if (!type || kindForCategory(type.category) !== 'instance') continue;
    present.add(entity.id);
    out.push(entity);
  }
  return out;
}

/**
 * Objekte, deren letzter Verweis aus den Texten einer Szene verschwunden ist: Vorher (`before`) verwies ein Text auf den Eintrag, jetzt
 * (`after`) keiner mehr – seine Instanz in der Szene fällt weg. Instanzen mit eigenen Angaben (zusätzliche Felder), Verbindungen oder
 * eigenem Inhalt bleiben, damit nichts verloren geht.
 */
export function unmentionedInstances(ctx: StoryCtx, node: StoryNode, before: string[], after: string[]): StoryNode[] {
  if (node.kind !== 'event' && node.kind !== 'period') return [];
  const refs = refIndex(ctx);
  const entitiesIn = (texts: string[]): Set<string> =>
    new Set(
      texts.flatMap((t) =>
        mentionsIn(t).flatMap((m) => {
          const e = refs.get(m.key);
          return e ? [e.id] : [];
        }),
      ),
    );
  const now = entitiesIn(after);
  const gone = new Set([...entitiesIn(before)].filter((id) => !now.has(id)));
  if (gone.size === 0) return [];
  return (ctx.children.get(node.id) ?? []).filter(
    (c) =>
      c.kind === 'instance' &&
      !!c.entityId &&
      gone.has(c.entityId) &&
      c.dynamicFields.length === 0 &&
      (ctx.links.get(c.id)?.length ?? 0) === 0 &&
      (ctx.children.get(c.id)?.length ?? 0) === 0,
  );
}

/** Fehlende Objekte aller Szenen: je Szene die Einträge, auf die ihre Texte verweisen, die aber noch nicht als Instanz in ihr liegen. */
export function missingCast(ctx: StoryCtx): { scene: StoryNode; entities: Entity[] }[] {
  return ctx.story.nodes
    .filter((n) => n.kind === 'event' || n.kind === 'period')
    .map((scene) => ({ scene, entities: missingMentioned(ctx, scene, textsOf(scene)) }))
    .filter((m) => m.entities.length > 0);
}

/** Freie Stellen für `count` neue Instanzen auf der Fläche eines Nodes: rechts neben dem am weitesten rechts liegenden freien Node, sonst am Ursprung. */
export function freeSpots(ctx: StoryCtx, nodeId: string, count: number): { x: number; y: number }[] {
  const children = ctx.children.get(nodeId) ?? [];
  const free = children.filter((c) => c.kind !== 'timeline' && c.x !== null && c.y !== null);
  let start: { x: number; y: number } = { x: 0, y: 0 };
  if (free.length > 0) {
    const right = free.reduce((a, b) => (b.x! > a.x! ? b : a));
    start = { x: right.x! + AUTO_INSTANCE_STEP, y: right.y! };
  }
  return Array.from({ length: count }, (_, i) => ({ x: start.x + i * AUTO_INSTANCE_STEP, y: start.y }));
}
